/**
 * Pure session-lifecycle decision logic, extracted from the SDK class so the
 * rules that decide when a session is restored, cleared, or rotated can be
 * unit-tested without a DOM or the rrweb recorder.
 *
 * History note (H-02, "every reload minted a new session"): init() used to run
 * two INDEPENDENT if-blocks — one restored the stored session when EITHER
 * last-activity OR last-send was fresh, and a second immediately cleared it
 * when EITHER was stale. A brand-new session always has lastSend === 0, so the
 * clear branch always fired and nulled the session that the restore branch had
 * just loaded; the SDK then asked the server for a fresh ID on every page
 * load. The two blocks are replaced by the single predicate below, used by
 * both the init restore path and the renewal check in sendData so the two
 * cannot disagree.
 */

/**
 * A session is alive when the MOST RECENT proof of life (user activity or a
 * successful send) is within the timeout window. Missing/never-happened
 * signals are treated as 0 and must never kill an otherwise fresh session.
 */
export function isSessionAlive(
  lastActivity: number,
  lastSendTime: number,
  sessionTimeout: number,
  now: number
): boolean {
  const lastAlive = Math.max(lastActivity || 0, lastSendTime || 0);
  return lastAlive + sessionTimeout > now;
}

/**
 * Whether the SDK should replace its current session ID with one returned by
 * the server. Two legitimate cases:
 *  - the SDK has no ID yet (first handshake response), or
 *  - the server explicitly rebound the session (sessionRebound: true, e.g. it
 *    could not verify the supplied ID's property binding and minted a fresh
 *    one). Before this, the SDK only adopted when its own ID was null, so
 *    every batch during a rebind window minted yet another server-side
 *    session while the SDK kept resending the stale ID.
 */
export function shouldAdoptServerSessionId(
  currentId: string | null,
  serverId: string | null | undefined,
  rebound: boolean
): boolean {
  if (!serverId) return false;
  if (!currentId) return true;
  return rebound && serverId !== currentId;
}

/**
 * Whether a follower tab should adopt a session ID another tab wrote to
 * localStorage. The server is authoritative for idle expiry (#223 / #222),
 * so we do not re-apply a local aliveness gate here — a freshly written ID
 * with stamps still at 0 must still be adoptable (#221 two-fresh-tabs).
 */
export function canAdoptStoredSession(
  storedId: string | null,
  currentId: string | null
): boolean {
  return Boolean(storedId && storedId !== currentId);
}

export type SessionVerdict = "reused" | "rotated" | "new";

/** Parse the server handshake verdict; anything else degrades to undefined. */
export function parseSessionVerdict(raw: unknown): SessionVerdict | undefined {
  return raw === "reused" || raw === "rotated" || raw === "new"
    ? raw
    : undefined;
}

/**
 * Whether a handshake response should clear the client-IP latch. Rotations
 * and ID changes need a fresh visitor-IP linkage; reuse must keep the latch.
 */
export function shouldResetClientIpLatch(
  verdict: SessionVerdict | undefined,
  previousId: string | null,
  newId: string | null | undefined
): boolean {
  if (verdict === "rotated") return true;
  return Boolean(newId && previousId && newId !== previousId);
}
